import { WellStatusTimeScale } from "constants/charts.enums";
import {
  wellStatusBucketDisplayLabel,
  wellStatusCustomGranularity,
  wellStatusDisplayName
} from "entities/charts/eva/requests/wellStatus";
import { removeCount } from "utils";

import { IChartResult } from "models/model";

import { ChartDataTableColumn, ChartDataTableRow } from "../hooks/useDataTableData";

const BUCKET_COLUMN_KEY = "bucket";
const GROUP_COLUMN_KEY = "group";
const TOTAL_COLUMN_KEY = "total";

// Descriptive bucket-column header per granularity — the generic "Time" reads as
// unhelpful next to labels like "2026-07". Year-Month and Week match the rig/frac
// activity table's wording so the two read the same.
//
// Custom has no header of its own: the server picks a granularity to fit the span,
// so the column is named for whatever it RESOLVED to (see
// wellStatusCustomGranularity). Hence Partial, with no Custom key — a Custom entry
// could only be a guess, and it is reachable only when the range is absent or
// malformed, exactly when a guess is most likely wrong. Omitting it makes the
// documented fallback (the backend's own axis title) the actual behaviour.
const BUCKET_COLUMN_HEADER: Partial<Record<WellStatusTimeScale, string>> = {
  [WellStatusTimeScale.Yearly]: "Year",
  [WellStatusTimeScale.Monthly]: "Year-Month",
  [WellStatusTimeScale.Weekly]: "Week",
  [WellStatusTimeScale.Daily]: "Day"
};

// NOTE: Changing this logic should result in changes to the chart-svc backend export logic.
/**
 * Raw data table for Well Status vs Time (product: "Add Raw data
 * table to chart"): one row per time bucket, first column the bucket label, then
 * one column per focus bin, then the stack TOTAL.
 *
 * The total column exists because the bars are stacked: what the user reads off
 * the chart is the summed height, and a table of only the parts would make them
 * add up 18 bins by hand. It is computed here rather than sent, so it can never
 * disagree with the columns beside it.
 *
 * `timeScale` drives the descriptive header for the bucket column — resolved
 * through the custom-range ladder first, so a Custom range is headed by the
 * granularity it actually drew at. When unknown it falls back to the backend
 * x-axis title (currently "Time").
 */
export const getWellStatusOverTimeDataTable = (
  responseData: IChartResult,
  timeScale?: Maybe<WellStatusTimeScale>,
  customRange?: Maybe<{ from?: Maybe<string>; to?: Maybe<string> }>
): { columns: ChartDataTableColumn[]; rows: ChartDataTableRow[] } => {
  const bucketLabels = (responseData?.layout?.xAxis?.data ?? []) as string[];
  const series = responseData?.series ?? [];

  const granularity =
    timeScale === WellStatusTimeScale.Custom
      ? wellStatusCustomGranularity(customRange?.from, customRange?.to) ?? timeScale
      : timeScale;
  const bucketColumnName =
    (granularity && BUCKET_COLUMN_HEADER[granularity]) ??
    responseData?.layout?.xAxis?.title ??
    "Time";

  const columns: ChartDataTableColumn[] = [
    { key: BUCKET_COLUMN_KEY, name: bucketColumnName, resizable: false },
    // Raw labels: on THIS chart a series is a focus bin ("Montney"), not a status,
    // so there is nothing to expand. (The current chart's All Statuses table does
    // map them — see below.)
    ...series.map((s) => ({ key: s.label, name: s.label, resizable: false })),
    { key: TOTAL_COLUMN_KEY, name: "Total", resizable: false }
  ];

  const rows: ChartDataTableRow[] = bucketLabels.map((label, bucketIndex) => {
    const row: ChartDataTableRow = {
      [BUCKET_COLUMN_KEY]: wellStatusBucketDisplayLabel(label)
    };
    let total = 0;
    series.forEach((s) => {
      const value = s.y?.[bucketIndex];
      const count = typeof value === "number" ? value : 0;
      total += count;
      row[s.label] = typeof value === "number" ? value : "";
    });
    row[TOTAL_COLUMN_KEY] = total;
    return row;
  });

  return { columns, rows };
};

/**
 * Raw data table for Current Well Status. Two shapes, matching the chart's two
 * modes:
 *
 * - filtered — one series per focus bin carrying a single count, so the table is
 *   one row per bin: (group, count);
 * - All Statuses — one series per STATUS spanning every bin, so the table is one
 *   row per bin with a column per status plus the row total, mirroring the
 *   stacked bar the user is reading.
 *
 * `isAllStatuses` comes from the REQUEST, not the response: a single-bin filtered
 * result and a single-status stacked one are indistinguishable by shape (the same
 * reason useWellStatusChartOptions reads the mode from the request).
 */
export const getWellStatusCurrentDataTable = (
  responseData: IChartResult,
  isAllStatuses: boolean,
  groupTitle?: Maybe<string>
): { columns: ChartDataTableColumn[]; rows: ChartDataTableRow[] } => {
  const series = responseData?.series ?? [];
  const groupColumnName = groupTitle || responseData?.layout?.xAxis?.title || "Group";
  const countColumnName = responseData?.layout?.yAxis?.title || "Well Count";

  if (!isAllStatuses) {
    const columns: ChartDataTableColumn[] = [
      { key: GROUP_COLUMN_KEY, name: groupColumnName, resizable: false },
      { key: TOTAL_COLUMN_KEY, name: countColumnName, resizable: false }
    ];
    const rows: ChartDataTableRow[] = series.map((s) => {
      const value = s.y?.[0];
      return {
        [GROUP_COLUMN_KEY]: removeCount(String(s.label ?? "")),
        [TOTAL_COLUMN_KEY]: typeof value === "number" ? value : ""
      };
    });
    return { columns, rows };
  }

  const binLabels = (responseData?.layout?.xAxis?.data ?? []) as string[];
  const columns: ChartDataTableColumn[] = [
    { key: GROUP_COLUMN_KEY, name: groupColumnName, resizable: false },
    // The chart's legend and tooltip render these same series through
    // wellStatusDisplayName ("PROD" -> "Producing"), so the table sitting under it
    // has to as well. The KEY stays the raw label, which is what row[s.label] below
    // and the response itself are keyed by.
    ...series.map((s) => ({
      key: s.label,
      name: wellStatusDisplayName(s.label),
      resizable: false
    })),
    { key: TOTAL_COLUMN_KEY, name: "Total", resizable: false }
  ];

  const rows: ChartDataTableRow[] = binLabels.map((label, binIndex) => {
    const row: ChartDataTableRow = { [GROUP_COLUMN_KEY]: removeCount(label) };
    let total = 0;
    series.forEach((s) => {
      const value = s.y?.[binIndex];
      const count = typeof value === "number" ? value : 0;
      total += count;
      row[s.label] = typeof value === "number" ? value : "";
    });
    row[TOTAL_COLUMN_KEY] = total;
    return row;
  });

  return { columns, rows };
};
